import { createSelector } from '@reduxjs/toolkit';
import { get } from 'lodash';

import { logger } from 'src/error';
import { getIsHotelOnly } from 'src/store/view/selectors';
import { PRICE_CHANGE_DIRECTION } from '../constants/actualization';

import { getIsOrderPrebuildFetching } from './order';
import { flightsCountSelector } from '../../CheckoutRoutes/PackageCheckout/store/flights/selectors';
import { getInProgress } from '../domains/tourists-checkout-validation/slice';
import { getIsManualPackage } from './package';

const getActualization = (state) => state.get('actualization');
export const getActualizationData = createSelector(
  getActualization,
  (act) => act && act.toJS(),
);
export const getActualizationFetching = (state) =>
  getActualization(state).get('fetching');
export const getActualizationRotten = (state) =>
  getActualization(state).get('rotten');
export const getActualizationSuccess = (state): boolean | null =>
  getActualization(state).get('success');
export const getActualizationShowModalCustomer = (state): boolean =>
  getActualization(state).get('show_modal_customer');
/**
 * Может использоваться как проверка на то, отработала ли первая актуализация,
 * так как до нее равно null
 */
export const getLastUpdated = (state): null | number =>
  getActualization(state).get('lastUpdated');

const _getHotelFacts = (state) => getActualization(state).get('hotelFacts');

export const getHotelFacts = createSelector(
  _getHotelFacts,
  (hotelFacts) => hotelFacts && hotelFacts.toJS(),
);

export const _getPriceChangesArray = (state) =>
  getActualization(state).getIn(['changes', 'net_price']);
export const getPriceChangesArray = createSelector(
  _getPriceChangesArray,
  (priceChangesArray) => priceChangesArray && priceChangesArray.toJS(),
);

/**
 * Проверяет, поменялась ли цена в туре
 */
export const getIsPriceChanged = createSelector(getPriceChangesArray, Boolean);

/**
 * Если цена поменялась, возвращает направление
 * @returns {string | undefined}
 */
export const getPriceChangeDirection = createSelector(
  [getPriceChangesArray, getIsPriceChanged],
  (changes, isPriceChanged) => {
    if (!isPriceChanged) return undefined;

    const [oldPrice, newPrice] = changes;
    return oldPrice < newPrice
      ? PRICE_CHANGE_DIRECTION.UP
      : PRICE_CHANGE_DIRECTION.DOWN;
  },
);

/**
 * Проверяет, изменилась ли цена более чем на 10%
 */
export const getIsPriceChangedSignificantly = createSelector(
  [getIsPriceChanged, getPriceChangesArray],
  (isChanged, changes) => {
    if (!isChanged) {
      return false;
    }
    try {
      const [oldPrice, newPrice] = changes;
      const priceDiff = Math.abs(newPrice - oldPrice);
      return priceDiff >= oldPrice * 0.1;
    } catch (error) {
      logger(error, 'Package price change');
      return false;
    }
  },
);

export const getShowPriceChanged = (state) =>
  getActualization(state).get('showPriceChanged');
export const getIsActualizationProcess = (state) =>
  getActualization(state).get('isActualizationProcess');
export const getIsActualizationOrdered = (state) =>
  getActualization(state).get('ordered');

export const getPrebookData = createSelector(
  getActualizationData,
  (actualization) => {
    const data = get(actualization, 'prebook_data', {});

    return data;
  },
);

export const getIsSubmitButtonLoading = (state) =>
  getActualizationFetching(state) ||
  getIsOrderPrebuildFetching(state) ||
  getInProgress(state);

export const actualizationExceptionSelector = (state): string | null =>
  getActualization(state).get('exception');

export const frontErrorSelector = (
  state,
): 'actualize_error' | 'network_error' | 'data_parsing_error' =>
  getActualization(state).get('frontError');

export const actualizationFetchingWithoutFlightsSelector = (state): boolean => {
  const isHotelOnly = getIsHotelOnly(state);
  const isManualPackage = getIsManualPackage(state);

  if (isHotelOnly || isManualPackage) return getActualizationFetching(state);

  return getActualizationFetching(state) || flightsCountSelector(state) === 0;
};
